"use no memo";

import { PrintRouteSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { PrintRoute } from "~/providers/printing-provider";
import { usePrinter } from "~/hooks/use-printer";
import { usePrintRouteManager } from "~/managers/use-print-route-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { MultiSelect } from "../ui/multi-select";

export default function PrintRouteForm({
  printRoute,
  onClose,
}: {
  printRoute?: PrintRoute;
  onClose: (force?: boolean) => void;
}) {
  const { printers } = usePrinter();

  const { createPrintRoute, updatePrintRoute } = usePrintRouteManager();
  const { setUnsavedChanges } = useUnsavedChanges();

  const { t, i18n } = useTranslation("common");

  const form = useForm({
    reValidateMode: "onChange",
    schema: PrintRouteSchema(i18n),
    defaultValues: {
      name: printRoute?.name ?? "",
      printerIds: printRoute?.printerIds ?? [],
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset({}, { keepValues: true });
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (printRoute) {
      const { data, error } = await updatePrintRoute({
        id: printRoute.id,
        ...formData,
      });
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const { data, error } = await createPrintRoute(formData);
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  return (
    <Form {...form}>
      <form
        id="print-route-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("PrintRoute.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="printerIds"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("PrintRoute.printers")}</FormLabel>
              <FormControl>
                <MultiSelect
                  placeholder="Select printers..."
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  options={printers.map((printer) => ({
                    label: printer.name,
                    value: printer.id,
                  }))}
                  defaultValue={field.value}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.formState.errors.root && (
          <FormMessage className="col-span-full">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
      </form>
    </Form>
  );
}
